import { openImpersonationSession } from 'utils/open-impersonation-session';
import React, { FC, Fragment, useCallback, useState } from 'react';
import { useQuery, useLazyQuery, NetworkStatus } from '@apollo/client';
import { Box } from '@mui/material';
import {
    getTechniciansForAdminUserListTemplate,
    getImpersonateUserTokenTemplate,
} from 'apollo/templates/user/query.graphql';
import AddTechniciansDialog from 'components/users/technicians/add-technicians-dialog/add-technicians-dialog.component';
import TechniciansList from 'components/users/technicians/technicians-list/technicians-list.component';
import debounce from 'lodash.debounce';
import { User, UserListFilter } from 'models/user';
import PageTitle from 'components/shared/page-title/page-title.component';
import Loader from 'components/shared/loader/loader.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import AdminTechniciansPageActions from 'components/users/technicians/technicians-page-actions/admin-technicians-page-actions.component';
import { useTranslation } from 'react-i18next';
import ImpersonateUserOrganizationsDialog from 'components/shared/impersonate-user-organizations-dialog/impersonate-user-organizations-dialog.component';

const AdminTechnicians: FC = () => {
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [lastOffset, setLastOffset] = useState(0);
    const { t: translate } = useTranslation(['users']);
    const [labId] = useState<string | null>(null);
    const [organizationsDialogOpen, setOrganizationsDialogOpen] = useState(false);
    const [user, setUser] = useState<User>({} as User);
    const [wlLabId, setWlLabId] = useState<string | undefined>();
    const [filter, setFilter] = useState<UserListFilter>({
        freeText: '',
        active: true,
    });
    const { showBoundary } = useDenthubErrorBoundary();

    const {
        data: { technicians } = {},
        loading: techniciansLoading,
        fetchMore,
        refetch,
        networkStatus,
    } = useQuery(getTechniciansForAdminUserListTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            labId: labId,
            filters: { freeText: '', active: true } as UserListFilter,
            limit: 25,
            offset: 0,
        },
        onCompleted: ({ technicians }) => {
            setLastOffset(technicians.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFetchMore = () => {
        fetchMore({
            variables: {
                offset: lastOffset,
            },
        });
    };

    const handleFilterChange = useCallback(
        debounce(({ freeText, active }: UserListFilter) => {
            setFilter({
                freeText: freeText,
                active: active,
            });
            refetch({
                filters: { freeText: freeText.toLocaleLowerCase(), active: active },
                offset: 0,
            });
            setLastOffset(0);
        }, 300),

        [],
    );

    const labFilterHandler = (filteredLabId: string | null) => {
        refetch({
            labId: filteredLabId,
            offset: 0,
        });
        setLastOffset(0);
    };

    const refetchAfterMutationHandler = () => {
        refetch({
            filters: { freeText: filter.freeText.toLocaleLowerCase(), active: filter.active },
            offset: 0,
        });
    };

    const [getImpersonateUserToken] = useLazyQuery(getImpersonateUserTokenTemplate, {
        fetchPolicy: 'network-only',
        onCompleted: ({ impersonateUser }) => {
            handleClose();
            openImpersonationSession(impersonateUser.token, wlLabId);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const dialogOpenHandler = () => {
        setIsDialogOpen(true);
    };

    const dialogCloseHandler = () => {
        setIsDialogOpen(false);
    };

    const handleImpersonateUserLogin = (user: User) => {
        setUser(user);
        setOrganizationsDialogOpen(true);
    };

    const handleClose = () => {
        setOrganizationsDialogOpen(false);
        setUser({} as User);
    };

    const handleConfirm = async (organizationId: string, wlLabId: string | undefined) => {
        if (wlLabId) {
            setWlLabId(wlLabId);
        }
        await getImpersonateUserToken({ variables: { userId: user._id, organizationId } });
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('common:TECHNICIANS')} />
            <Box mb={1}>
                <AdminTechniciansPageActions
                    onFilterChange={handleFilterChange}
                    onDialogOpenClick={dialogOpenHandler}
                    onLabChange={labFilterHandler}
                />
            </Box>
            {techniciansLoading && networkStatus !== NetworkStatus.fetchMore ? (
                <Loader />
            ) : (
                <Fragment>
                    <TechniciansList
                        technicians={technicians}
                        isTechniciansLoading={techniciansLoading}
                        onFetchMoreChange={handleFetchMore}
                        onUpdateTechnicianClick={refetchAfterMutationHandler}
                        onLoginClick={handleImpersonateUserLogin}
                    />
                    <AddTechniciansDialog
                        isDialogOpen={isDialogOpen}
                        onDialogCloseClick={dialogCloseHandler}
                        onAddTechnicianClick={refetchAfterMutationHandler}
                    />
                </Fragment>
            )}
            {organizationsDialogOpen ? (
                <ImpersonateUserOrganizationsDialog
                    open={organizationsDialogOpen}
                    linkedOrganizations={user.linkedOrganizations}
                    handleClose={handleClose}
                    handleConfirm={handleConfirm}
                />
            ) : undefined}
        </Fragment>
    );
};

export default AdminTechnicians;
